/**
 * Canonical Paper data model.
 *
 * A Paper is a first-class entity representing a single exam paper. It is
 * uniquely identified by `(brand, paperCode, series)` — deliberately mirroring
 * the question parent-group identity (`buildParentQuestionId`) minus the
 * question-number root, so a paper and the ribbon groups that belong to it
 * share one identity scheme.
 *
 * The Paper is the authoritative home of the paper-level facts a question
 * inherits: `subject`, `level` (qualification), `tier`, `series` and the paper
 * `date`. A question references its paper by `paperId` and does not store these
 * itself — see {@link Question}. The paper date is the single temporal fact;
 * the calendar year used by search is derived from it.
 *
 * This is the foundation for paper management becoming its own domain (e.g.
 * uploading a past paper and automating question-content extraction). For now
 * a Paper carries a set of bilingual attachments (English + Welsh PDFs) such as
 * the question paper, a data booklet or source material.
 *
 * @format
 */

import type { Brand } from "./config/brands";
import type { QuestionSeries } from "./question";

/**
 * Reference to a Core Taxonomy entity (QB2-223 / DR10 / DR11). `code` is the
 * stable Core key and is authoritative; `uuid` is the Core entity id; `title`
 * is the resolved English display label, stored for convenience (Core remains
 * the source of truth). Used for qualification, tier and series season.
 */
export interface CoreRef {
	code: string;
	uuid?: string;
	title?: string;
}

/** @deprecated alias retained for readability; use {@link CoreRef}. */
export type PaperQualification = CoreRef;

/**
 * A single attachment on a paper. A paper may carry several (e.g. the question
 * paper plus a data booklet), and each attachment holds an English and/or Welsh
 * PDF. The `id` is stable for the lifetime of the attachment so future features
 * (e.g. flagging that a question relies on a specific attachment) can reference
 * it directly.
 */
export interface PaperAttachment {
	/** Stable identifier for this attachment within the paper (UUID). */
	id: string;
	/** Human-readable label, e.g. "Question paper", "Data booklet". */
	label?: string;
	/** S3 asset key for the English PDF. */
	en?: string;
	/** S3 asset key for the Welsh PDF. */
	cy?: string;
}

/**
 * Canonical Paper record. Identity is `(brand, paperCode, series)`; see
 * {@link buildPaperId}.
 */
export interface Paper {
	/** Deterministic id: `{brand}--{paperCode}--{season-year}` (lowercased). */
	id: string;
	brand: Brand;
	paperCode: string;
	series: QuestionSeries;
	/**
	 * Paper date (ISO-8601). The single authoritative temporal fact for the
	 * paper and the questions that belong to it; the calendar year used by
	 * search is derived from this. A question no longer carries its own date.
	 */
	date: string;
	/**
	 * Subject(s) the paper covers. Authoritative paper-level fact inherited by
	 * every question in the paper.
	 */
	subject: string | string[];
	/**
	 * Qualification of the paper (QB2-223). A reference to a Core Taxonomy
	 * qualification entity, not the old GCSE/AS/A-Level enum — `code` is the
	 * authoritative Core key (e.g. `alevel`), `title` is the resolved display
	 * label. Authoritative paper-level fact inherited by every question.
	 */
	qualification: CoreRef;
	/**
	 * Tier of the paper (DR11). A Core Taxonomy tier reference (code + optional
	 * uuid/title), not the old enum. Optional where a paper has no tier.
	 */
	tier?: CoreRef;
	/** Bilingual attachments (PDFs) for the paper. May be empty. */
	attachments: PaperAttachment[];
	/**
	 * Number of root questions in this paper (i.e. ribbon parent groups). Derived
	 * at list time from the question set, not persisted — indicates how many
	 * questions will surface the paper's attachments.
	 */
	rootQuestionCount?: number;
	/** ISO-8601 timestamp recording when the paper record was first created. */
	createdAt?: string;
	/** ISO-8601 timestamp recording the most recent modification. */
	updatedAt?: string;
	/** Identifier of the user (or system actor) that created the record. */
	createdBy?: string;
	/** Identifier of the user (or system actor) that last modified the record. */
	updatedBy?: string;
}

/**
 * Build the deterministic paper identifier.
 *
 * Shape: `{brand}--{paperCode}--{season-year}` (lowercased). This is exactly
 * the question parent-group id (`buildParentQuestionId`) without the trailing
 * `--{root}`, so the paper a question belongs to can be derived from its
 * `parentQuestionId` by dropping the final segment.
 */
export function buildPaperId(input: {
	brand: string;
	paperCode: string;
	series: { season: string; year: number };
}): string {
	return [input.brand, input.paperCode, `${input.series.season}-${input.series.year}`]
		.map((part) => String(part).trim())
		.join("--")
		.toLowerCase();
}

/**
 * Derive the owning paper id from a question's `parentQuestionId` by dropping
 * the trailing `--{root}` segment. Returns `null` when the input does not look
 * like a parent-group id.
 */
export function getPaperIdFromParentQuestionId(parentQuestionId: string): string | null {
	const trimmed = parentQuestionId.trim();
	const lastSep = trimmed.lastIndexOf("--");
	if (lastSep === -1) {
		return null;
	}
	return trimmed.slice(0, lastSep).toLowerCase();
}
